import { PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/styles';
import { Divider, message, Spin, Table, Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PromotionEmpty as PromotionEmptyIcon,
  Specific as SpecificIcon,
} from 'common/components/svgs';
import { formatPriceWithSpace } from '~helpers/currency';
import { dayFormatType, formatDay } from '~helpers/day';
import PromotionPlanEditor from '~pages/dashboard/referral/promotion/promotion-editor';
import PromotionPlanViewer from '~pages/dashboard/referral/promotion/promotion-viewer';
import { QUERY_PROMOTION_PLANS } from '~pages/dashboard/referral/promotion/queries';
import showElementByAuth from '~helpers/auth';
import { platformEntity, entityAction } from '~constants';
import { PAYMENT_PREFERENCE } from '~constants/referral';

const useStyles = makeStyles(
  theme => ({
    tagScheduled: {
      textTransform: 'capitalize',
      background: theme.colors.orange1,
      border: '1px solid',
      borderColor: theme.colors.orange3,
      color: theme.colors.orange6,
    },
    tagActive: {
      textTransform: 'capitalize',
      background: theme.colors.green1,
      border: '1px solid',
      borderColor: theme.colors.green3,
      color: theme.colors.green6,
    },
    tagExpired: {
      textTransform: 'capitalize',
      background: theme.colors.gray2,
      border: '1px solid',
      borderColor: theme.colors.gray5,
      color: 'rgba(0, 0, 0, 0.45)',
    },
  }),
  { name: 'PromotionTags' },
);

const Promotion = () => {
  const { t } = useTranslation();

  const classes = useStyles();
  const [planEditorType, setPlanEditorType] = useState('create');
  const [editingPlan, setEditingPlan] = useState(null);
  const [viewingPlan, setViewingPlan] = useState(null);
  const [showPlanEditor, setShowPlanEditor] = useState(false);
  const [showPlanViewer, setShowPlanViewer] = useState(false);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  const {
    loading,
    data: commissionPlanListResponse,
    refetch: refetchCommissionPlan,
  } = useQuery(QUERY_PROMOTION_PLANS, {
    variables: {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      sortBy: [
        { sortField: 'DEFAULT', sortDirection: 'DESCENDING' },
        { sortField: 'UPDATED_AT', sortDirection: 'DESCENDING' },
      ],
    },
    onError: e => {
      message.error(e?.toString());
    },
  });

  const commissionPlanList = useMemo(() => {
    return commissionPlanListResponse?.referralCommissionPlans?.edges || [];
  }, [commissionPlanListResponse]);

  const listTotalCount = useMemo(() => {
    return commissionPlanListResponse?.referralCommissionPlans?.totalCount || 0;
  }, [commissionPlanListResponse]);

  const handleCloseEditorModal = () => {
    setShowPlanEditor(false);
    refetchCommissionPlan();
  };

  const handleCloseViewerModal = () => {
    setShowPlanViewer(false);
  };

  const handleCreatePlan = () => {
    setPlanEditorType('create');
    setEditingPlan(null);
    setShowPlanEditor(true);
  };

  const handleEditPlan = plan => {
    setPlanEditorType('edit');
    setEditingPlan(plan);
    setShowPlanEditor(true);
  };

  const handleViewPlan = plan => {
    setViewingPlan(plan);
    setShowPlanViewer(true);
  };

  const tableColumns = useMemo(() => {
    return [
      {
        title: t('cms.referral.promotion.list.column.name'),
        dataIndex: 'node.name',
        key: 'name',
        className: 'promotion-list__body__list__column',
        render: (text, record) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span title={text} className="promotion-list__body__list__column-body">
              {text || '-'}
            </span>

            {record.node?.partnerReferralCommissionPlans?.length > 0 && (
              <Tooltip
                title={'This promotion is for specific partners only'}
                placement="top"
                className="promotion-list__body__list__column-header__tooltip"
              >
                <SpecificIcon
                  style={{ height: '16px', width: '16px', marginLeft: '8px', flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: (
          <div className="promotion-list__body__list__column-header">
            {t('cms.referral.promotion.list.column.applied_country')}
            <Tooltip
              title={t('cms.referral.promotion.list.tooltip.applied_country')}
              placement="top"
              className="promotion-list__body__list__column-header__tooltip"
            >
              <QuestionCircleOutlined className="promotion-list--ml-4 promotion-list__body__list__column-header__icon" />
            </Tooltip>
          </div>
        ),
        dataIndex: 'node.country.name',
        key: 'appliedCountry',
        className: '',
        render: text => (
          <span title={text} className="promotion-list__body__list__column-body">
            {text || '-'}
          </span>
        ),
      },
      {
        title: (
          <div className="promotion-list__body__list__column-header">
            Partner Payment Preference
          </div>
        ),
        width: 120,
        dataIndex: 'node.paymentPreference',
        key: 'paymentPreference',
        className: '',
        render: text => (
          <span title={text} className="promotion-list__body__list__column-body">
            {text ? PAYMENT_PREFERENCE[text] : '-'}
          </span>
        ),
      },
      {
        title: (
          <div className="promotion-list__body__list__column-header">
            {t('cms.referral.promotion.list.column.long_term')}
            <Tooltip
              title={t('cms.referral.promotion.list.tooltip.long_term')}
              placement="top"
              className="promotion-list__body__list__column-header__tooltip"
            >
              <QuestionCircleOutlined className="promotion-list--ml-4 promotion-list__body__list__column-header__icon" />
            </Tooltip>
          </div>
        ),
        dataIndex: 'node.tiers[0].longTermCalculationValue',
        key: 'longTerm',
        className:
          'promotion-list__body__list__column promotion-list__body__list__column--text-right',
        render: (text, record) => (
          <span className="promotion-list__body__list__column-body promotion-list--text-right">
            {text && record.node.currency
              ? `${formatPriceWithSpace(text, record.node.currency)}`
              : '-'}
          </span>
        ),
      },
      {
        title: (
          <div className="promotion-list__body__list__column-header">
            {t('cms.referral.promotion.list.column.short_term')}
            <Tooltip
              title={t('cms.referral.promotion.list.tooltip.short_term')}
              placement="top"
              className="promotion-list__body__list__column-header__tooltip"
            >
              <QuestionCircleOutlined className="promotion-list--ml-4 promotion-list__body__list__column-header__icon" />
            </Tooltip>
          </div>
        ),
        dataIndex: 'node.tiers[0].shortTermCalculationValue',
        key: 'shortTerm',
        className:
          'promotion-list__body__list__column promotion-list__body__list__column--text-right',
        render: (text, record) => (
          <span className="promotion-list__body__list__column-body promotion-list--text-right">
            {text && record.node.currency
              ? `${formatPriceWithSpace(text, record.node.currency)}`
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.referral.promotion.list.column.valid_date'),
        key: 'validDate',
        className: 'promotion-list__body__list__column',
        render: (text, record) => (
          <span className="promotion-list__body__list__column-body">
            {`${formatDay(dayFormatType.UKFORMAT, record.node.validDateFrom)} - ${formatDay(
              dayFormatType.UKFORMAT,
              record.node.validDateTo,
            )}`}
          </span>
        ),
      },
      {
        title: t('cms.referral.promotion.list.column.status'),
        dataIndex: 'node.status',
        key: 'status',
        className: '',
        render: text => {
          return (
            <Tag
              className={classNames('promotion-list__body__list__tag', {
                [classes.tagActive]: text === 'ACTIVE',
                [classes.tagScheduled]: text === 'SCHEDULED',
                [classes.tagExpired]: text === 'EXPIRED',
              })}
            >
              {text.toLowerCase() || '-'}
            </Tag>
          );
        },
      },
      {
        title: t('cms.referral.promotion.list.column.actions'),
        dataIndex: 'actions',
        key: 'actions',
        fixed: 'right',
        width: 118,
        render: (text, record) => (
          <div className="promotion-list__body__list__column-body">
            <If
              condition={showElementByAuth(
                platformEntity.REFERRAL_COMMISSION_PLAN,
                entityAction.CREATE,
              )}
            >
              <a role="button" tabIndex="0" onClick={() => handleEditPlan(record.node)}>
                Edit
              </a>
              <Divider type="vertical" />
            </If>
            <a role="button" tabIndex="1" onClick={() => handleViewPlan(record.node)}>
              View
            </a>
          </div>
        ),
      },
    ];
  }, []);
  return (
    <>
      <div className="promotion-list">
        <div className="promotion-list__header">
          <h1>{t('cms.referral.promotion.title')}</h1>
          <If
            condition={showElementByAuth(
              platformEntity.REFERRAL_COMMISSION_PLAN,
              entityAction.CREATE,
            )}
          >
            {commissionPlanList.length ? (
              <button
                className="promotion-list__header__button"
                type="button"
                onClick={handleCreatePlan}
              >
                <PlusOutlined
                  className="promotion-list__header__button__icon"
                  style={{ fontSize: '12px', marginLeft: '4px' }}
                />
                {t('cms.referral.promotion.button.add')}
              </button>
            ) : null}
          </If>
        </div>

        <section
          className={classNames('promotion-list__body', {
            'promotion-list__body--bg-white': commissionPlanList.length === 0 && !loading,
          })}
        >
          <Choose>
            <When condition={loading}>
              <div className="promotion-list__spin-container">
                <Spin />
              </div>
            </When>
            <When condition={commissionPlanList.length}>
              <Table
                columns={tableColumns}
                className="table-list  promotion-list__body__list"
                dataSource={commissionPlanList}
                rowKey={record => record.node.id}
                // scroll={{ x: '130%' }}
                pagination={{
                  current: pagination.pageNumber,
                  pageSize: pagination.pageSize,
                  showSizeChanger: true,
                  hideOnSinglePage: false,
                  total: listTotalCount,
                }}
                loading={loading}
                onChange={newestPagination =>
                  setPagination({
                    pageNumber: newestPagination.current,
                    pageSize: newestPagination.pageSize,
                  })
                }
              />
            </When>
            <When condition={commissionPlanList.length === 0}>
              <div className="promotion-list__blank-tips">
                <div className="promotion-list__blank-tips__content">
                  <div>
                    <PromotionEmptyIcon />
                  </div>
                  <span className="promotion-list__blank-tips__tip">
                    {t('cms.referral.promotion.blanktip')}
                  </span>
                  <If
                    condition={showElementByAuth(
                      platformEntity.REFERRAL_COMMISSION_PLAN,
                      entityAction.CREATE,
                    )}
                  >
                    <button
                      className="promotion-list__header__button"
                      type="button"
                      onClick={handleCreatePlan}
                    >
                      {t('cms.referral.promotion.button.create')}
                    </button>
                  </If>
                </div>
              </div>
            </When>
          </Choose>
        </section>

        <If condition={showPlanEditor}>
          <PromotionPlanEditor
            modalType={planEditorType}
            editPlan={editingPlan}
            handleCloseModal={handleCloseEditorModal}
          />
        </If>

        <If condition={showPlanViewer}>
          <PromotionPlanViewer
            activeModal
            viewPlan={viewingPlan}
            t={t}
            handleCloseModal={handleCloseViewerModal}
          />
        </If>
      </div>
    </>
  );
};

export default Promotion;
